import "./MergeFileModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { TextInput } from "components/inputs";
import Table from "components/Table/Table";
import { commonColumnProps, PAGE_SIZE } from "constants/TableConstants";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetFilesQuery } from "services/file-api";
import type { FileType } from "types/DataTypes";
import { getDateFromISOStringDatePart } from "utils/date-utils";

import KaboodleModal from "./KaboodleModal";

const SEARCH_DEBOUNCE_MS = 400;

export type MergeCandidateRow = {
  id: number;
  client: string;
  claimant: string;
  date_opened: string;
  services: string;
  status: string;
};

interface MergeFileModalProps {
  isOpen: boolean;
  // the (unprocessed) file being merged away; excluded from the results
  currentFile: FileType | null | undefined;
  isMerging?: boolean;
  onClose: () => void;
  onConfirm: (targetFileId: number) => void;
}

export const formatClaimantName = (file: FileType): string => {
  const claimant = file.claimants?.[0];
  if (!claimant) return "";
  return [claimant.first_name, claimant.last_name].filter(Boolean).join(" ");
};

// a file can carry several services of the same type (e.g. two MSAs), so
// de-duplicate on the label; labels come from the backend's SERVICE_TYPE
// choices, so equal types always render the same string
export const formatServices = (file: FileType): string =>
  [
    ...new Set(
      (file.services ?? []).map(
        (service) => service.type_verbose || service.type,
      ),
    ),
  ].join(", ");

// files keyed in by operations open on a later transition; unprocessed ones
// have no opened date yet, so fall back to when the file was created
export const formatDateOpened = (file: FileType): string =>
  file.datetime_opened
    ? getDateFromISOStringDatePart(file.datetime_opened)
    : `Created ${getDateFromISOStringDatePart(file.created)}`;

export const toMergeCandidateRow = (file: FileType): MergeCandidateRow => ({
  id: file.id,
  client: file.client?.name ?? "",
  claimant: formatClaimantName(file),
  date_opened: formatDateOpened(file),
  services: formatServices(file),
  status: file.status_verbose,
});

// only the short columns are pinned; client and services share the rest
// (the selected-file panel below shows their full values)
const COLUMNS = [
  { key: "id", name: "ID", ...commonColumnProps, width: 64 },
  { key: "client", name: "Client", ...commonColumnProps },
  { key: "claimant", name: "Claimant", ...commonColumnProps, width: 130 },
  { key: "date_opened", name: "Date Opened", ...commonColumnProps, width: 150 },
  { key: "services", name: "Services", ...commonColumnProps },
  { key: "status", name: "Status", ...commonColumnProps, width: 110 },
];

/**
 * Search for the prior file an unprocessed file should be merged into. The
 * user picks a row, reviews the key details of both files, then confirms.
 */
const MergeFileModal = ({
  isOpen,
  currentFile,
  isMerging = false,
  onClose,
  onConfirm,
}: MergeFileModalProps) => {
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<FileType | null>(null);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value.trim());
        setPage(1);
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(
    () => () => {
      debouncedSetSearchTerm.cancel();
    },
    [debouncedSetSearchTerm],
  );

  // start from a clean slate every time the modal opens
  useEffect(() => {
    if (isOpen) {
      setInputValue("");
      setSearchTerm("");
      setPage(1);
      setSelected(null);
    }
  }, [isOpen]);

  // no browsing: the user must name the file they want (claimant, client,
  // claim number, id) rather than page through every file in the org, which
  // is both faster and less likely to end in merging into the wrong file
  const { data, isFetching } = useGetFilesQuery(
    {
      searchTerm,
      sort_by: "-id",
      filters: {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        // excluded server-side so the page size and count stay exact
        ...(currentFile?.id && { exclude_id: currentFile.id }),
      },
    },
    { skip: !isOpen || searchTerm.length === 0 },
  );

  const candidates = useMemo(() => data?.results ?? [], [data?.results]);

  const rows = useMemo(() => candidates.map(toMergeCandidateRow), [candidates]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
      debouncedSetSearchTerm(event.target.value);
    },
    [debouncedSetSearchTerm],
  );

  const handleCellClick = useCallback(
    ({ row }: { row: MergeCandidateRow }) => {
      setSelected(candidates.find((file) => file.id === row.id) ?? null);
    },
    [candidates],
  );

  const handleConfirm = useCallback(() => {
    if (selected) {
      onConfirm(selected.id);
    }
  }, [selected, onConfirm]);

  const selectedClaimant = selected ? formatClaimantName(selected) : "";
  const selectedServices = selected ? formatServices(selected) : "";

  const emptyMessage =
    searchTerm.length === 0
      ? "Search by claimant, client, claim number, or file ID to find the file to merge into."
      : "No matching files found.";

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      size="extra-large"
      className="merge-file-modal-container"
      footer={
        <div className="flex jc-c">
          <Button
            className="add"
            data-testid="confirm-merge-button"
            onClick={handleConfirm}
            disabled={!selected || isMerging}
          >
            {isMerging
              ? "Merging..."
              : selected
                ? `Merge into File #${selected.id}`
                : "Merge"}
          </Button>
          <Button onClick={onClose} disabled={isMerging}>
            Cancel
          </Button>
        </div>
      }
    >
      <Card heading="Merge File">
        <div className="merge-file-modal">
          <p className="merge-file-modal__intro">
            {currentFile
              ? `Merge File #${currentFile.id} into an existing file. `
              : ""}
            The file you pick will receive an update note, copies of every
            document (as Updated Medical Records), and this file&apos;s tasks.
            This file will then be archived.
          </p>

          <TextInput
            name="merge-file-search"
            label="Find file"
            placeholder="Claimant name, client, claim number, SSN, or file ID"
            value={inputValue}
            onChange={handleSearchChange}
            data-testid="merge-file-search-input"
            autoFocus
          />

          <div className="merge-file-modal__results">
            <Table
              data-testid="merge-file-results"
              columns={COLUMNS}
              rows={rows}
              rowKeyGetter={(row: MergeCandidateRow) => row.id}
              onCellClick={handleCellClick}
              isLoading={isFetching}
              isSortable={false}
              isResizable={false}
              variant="compact"
              emptyTableMessage={emptyMessage}
              rowClass={(row: MergeCandidateRow) =>
                row.id === selected?.id
                  ? "merge-candidate-row--selected"
                  : undefined
              }
              pagination={{
                pageSize: PAGE_SIZE,
                pageIndex: page,
                rowCount: data?.count ?? 0,
                onPageChange: setPage,
                isLoading: isFetching,
              }}
            />
          </div>

          {selected && (
            <div
              className="merge-file-modal__selected"
              data-testid="merge-file-selected"
            >
              <strong>Merge into File #{selected.id}</strong>
              <ul>
                {selected.client?.name && (
                  <li>Client: {selected.client.name}</li>
                )}
                {selectedClaimant && <li>Claimant: {selectedClaimant}</li>}
                <li>Date Opened: {formatDateOpened(selected)}</li>
                {selectedServices && <li>Services: {selectedServices}</li>}
                <li>Status: {selected.status_verbose}</li>
              </ul>
              <p className="merge-file-modal__warning">
                This cannot be undone. File #{currentFile?.id} will be archived.
              </p>
            </div>
          )}
        </div>
      </Card>
    </KaboodleModal>
  );
};

export default MergeFileModal;
